import { AppTextInput, AppNumberInput } from 'components/FormElements';
import {
  Controller,
  useFormContext,
  useWatch,
  useFieldArray,
} from 'react-hook-form';
import Table from 'components/Table';
import { AppSimpleSelect } from 'components/FormElements';
import { numberToCurrencyString } from 'further-ui/utils';
import { Delete } from '@mui/icons-material';
import Button from 'components/Button';
import { Tooltip, useTheme, styled } from '@mui/material';
import ButtonGroup from 'components/ButtonGroup';
import { SelectChangeEvent } from '@mui/material';
import {
  LpAccrualFrequency,
  LpFeeCalculationBasis,
  LpFeeCommitmentStatus,
  LpFeeType,
} from 'further-types/lp/lp-fee-template';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { InferredFeeSettingsFormType } from './FormWrapper';
import { emptyFee } from './helpers';
import {
  LpFeeCalculationBasisLabels,
  LpFeeCommitmentStatusLabels,
} from 'further-ui/labels';
import Spacing from 'components/Spacing';

const TextContainer = styled('div')({
  height: '29px',
  // align with other elements without border
  padding: '9px 12px 9px 0px',
  borderRadius: '4px',
});

const StyledAppNumberInput = styled(AppNumberInput)<{
  hasApprovedFees?: boolean;
}>(({ theme, hasApprovedFees = false }) => ({
  backgroundColor: hasApprovedFees ? theme.palette.grey[100] : 'transparent',
}));

const feeTypeOptions = [
  {
    label: 'One-off fixed',
    value: LpFeeType.OneOffFixed,
  },
  {
    label: 'Recurring %',
    value: LpFeeType.RecurringPercentage,
  },
];

const calculationBasisOptions = [
  {
    label:
      LpFeeCalculationBasisLabels.get(LpFeeCalculationBasis.CommittedCapital) ??
      '',
    value: LpFeeCalculationBasis.CommittedCapital,
  },
  {
    label:
      LpFeeCalculationBasisLabels.get(LpFeeCalculationBasis.NetCalledCapital) ??
      '',
    value: LpFeeCalculationBasis.NetCalledCapital,
  },
  {
    label:
      LpFeeCalculationBasisLabels.get(
        LpFeeCalculationBasis.TotalAcquisitionCost,
      ) ?? '',
    value: LpFeeCalculationBasis.TotalAcquisitionCost,
  },
  {
    label:
      LpFeeCalculationBasisLabels.get(
        LpFeeCalculationBasis.UnrealisedAssetCost,
      ) ?? '',
    value: LpFeeCalculationBasis.UnrealisedAssetCost,
  },
];

const accrualFrequencyOptions = [
  {
    label: 'Quarterly advance',
    value: LpAccrualFrequency.QuarterlyAdvance,
  },
  {
    label: 'Monthly advance',
    value: LpAccrualFrequency.MonthlyAdvance,
  },
  {
    label: 'Bi-annually advance',
    value: LpAccrualFrequency.BiannualyAdvance,
  },
  {
    label: 'Annually advance',
    value: LpAccrualFrequency.AnnuallyAdvance,
  },
  {
    label: 'Quarterly arrears',
    value: LpAccrualFrequency.QuarterlyArrears,
  },
  {
    label: 'Monthly arrears',
    value: LpAccrualFrequency.MonthlyArrears,
  },
  {
    label: 'Bi-annually arrears',
    value: LpAccrualFrequency.BiannualyArrears,
  },
  {
    label: 'Annually arrears',
    value: LpAccrualFrequency.AnnuallyArrears,
  },
];

const commitmentStatusOptions = [
  {
    label:
      LpFeeCommitmentStatusLabels.get(LpFeeCommitmentStatus.InsideCommitment) ??
      '',
    value: LpFeeCommitmentStatus.InsideCommitment,
  },
  {
    label:
      LpFeeCommitmentStatusLabels.get(
        LpFeeCommitmentStatus.OutsideCommitment,
      ) ?? '',
    value: LpFeeCommitmentStatus.OutsideCommitment,
  },
];

type LpFee =
  InferredFeeSettingsFormType['lpFeeTemplates'][number]['lpFeeTemplateFees'][number];

const FeeTemplateFeesTable: React.FC<{
  index: number;
  hasApprovedFees: boolean;
  templateInUseMessage?: string;
  hasCommitments: boolean;
  setHasEditedTemplateWithCommitment: (
    hasEditedTemplateWithCommitment: boolean,
  ) => void;
}> = ({
  index,
  templateInUseMessage,
  hasApprovedFees,
  hasCommitments,
  setHasEditedTemplateWithCommitment,
}) => {
  const { vehicle } = useVehicle();
  const { control, setValue, resetField, getValues, reset } =
    useFormContext<InferredFeeSettingsFormType>();
  const theme = useTheme();

  const { append } = useFieldArray({
    control,
    name: `lpFeeTemplates.${index}.lpFeeTemplateFees`,
  });

  const fees = useWatch({
    control,
    name: `lpFeeTemplates.${index}.lpFeeTemplateFees`,
  });

  const calculateFeeIncTax = (feeExTax: number, salesTaxRate: number) => {
    return feeExTax * (1 + salesTaxRate / 100);
  };
  const disabledUntilLaterDate = true;

  const columns = [
    {
      label: 'Fee name',
      key: 'name',
      sort: false,
      render: (_, i) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.name`}
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <AppTextInput
                {...field}
                placeholder="Enter fee name"
                fullWidth
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                size="small"
                style={{ minWidth: 200 }}
                disabled={hasApprovedFees}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Fee type',
      key: 'type',
      sort: false,
      render: (_, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.type`}
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <AppSimpleSelect
                {...field}
                options={feeTypeOptions}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  if (e.target.value === LpFeeType.RecurringPercentage) {
                    resetField(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.accrualFrequency`,
                    );
                    resetField(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.calculationBasis`,
                    );
                    setValue(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.startPeriod`,
                      0,
                    );
                    setValue(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.endPeriod`,
                      0,
                    );
                  }

                  if (e.target.value === LpFeeType.OneOffFixed) {
                    resetField(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.accrualFrequency`,
                    );
                    resetField(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.calculationBasis`,
                    );
                    setValue(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.startPeriod`,
                      0,
                    );
                    setValue(
                      `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.endPeriod`,
                      0,
                    );
                  }

                  field.onChange(e);
                }}
                variant="outlined"
                error={!!error}
                style={{ minWidth: 120 }}
                errorMessage={error?.message}
                disabled={hasApprovedFees}
                sx={{
                  backgroundColor: hasApprovedFees
                    ? theme.palette.grey[100]
                    : 'transparent',
                }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Calculation basis',
      key: 'calculationBasis',
      sort: false,
      render: (elm: LpFee, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.calculationBasis`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {elm.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage}>
                  <AppSimpleSelect
                    {...field}
                    options={calculationBasisOptions}
                    variant="outlined"
                    error={!!error}
                    errorMessage={error?.message}
                    disabled={disabledUntilLaterDate}
                    sx={{
                      backgroundColor: hasApprovedFees
                        ? theme.palette.grey[100]
                        : 'transparent',
                    }}
                  />
                </Tooltip>
              ) : (
                <TextContainer>
                  {LpFeeCalculationBasisLabels.get(LpFeeCalculationBasis.Fixed)}
                </TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'Accrual frequency',
      key: 'accrualFrequency',
      sort: false,
      render: (elm: LpFee, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.accrualFrequency`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {elm.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage}>
                  <AppSimpleSelect
                    {...field}
                    value={field.value}
                    options={accrualFrequencyOptions}
                    variant="outlined"
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      field.onChange(e);
                    }}
                    error={!!error}
                    disabled={hasApprovedFees}
                    errorMessage={error?.message}
                    sx={{
                      backgroundColor: hasApprovedFees
                        ? theme.palette.grey[100]
                        : 'transparent',
                      minWidth: 140,
                    }}
                  />
                </Tooltip>
              ) : (
                <TextContainer>N/A</TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'Inside/outside commitment',
      key: 'commitmentStatus',
      sort: false,
      render: (_, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.commitmentStatus`}
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <AppSimpleSelect
                {...field}
                options={commitmentStatusOptions}
                variant="outlined"
                disabled={hasApprovedFees}
                error={!!error}
                errorMessage={error?.message}
                sx={{
                  backgroundColor: hasApprovedFees
                    ? theme.palette.grey[100]
                    : 'transparent',
                }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Start period',
      key: 'startPeriod',
      sort: false,
      render: (elm: LpFee, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.startPeriod`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {elm.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage}>
                  <StyledAppNumberInput
                    {...field}
                    placeholder="e.g. 1"
                    fullWidth
                    disabled={hasApprovedFees}
                    error={!!error}
                    helperText={error?.message}
                    hasApprovedFees={hasApprovedFees}
                  />
                </Tooltip>
              ) : (
                <TextContainer>N/A</TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'Finish period',
      key: 'finishingPeriod',
      sort: false,
      render: (elm: LpFee, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.endPeriod`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {elm.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage}>
                  <StyledAppNumberInput
                    {...field}
                    placeholder="e.g. 1"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    disabled={hasApprovedFees}
                    hasApprovedFees={hasApprovedFees}
                  />
                </Tooltip>
              ) : (
                <TextContainer>N/A</TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'Fee (ex tax)',
      key: 'netFee',
      sort: false,
      render: (elm: LpFee, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.feeExTax`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <div>
                <AppNumberInput
                  {...field}
                  onChange={(value: number) => {
                    field.onChange(value);
                  }}
                  decimals={10}
                  minFractionDigits={2}
                  error={!!error}
                  disabled={hasApprovedFees}
                  helperText={error?.message}
                  currencyStartAdornment={elm?.type === LpFeeType.OneOffFixed}
                  currency={vehicle?.baseCurrency}
                  percentEndAdornment={
                    elm?.type === LpFeeType.RecurringPercentage
                  }
                  style={{
                    backgroundColor: hasApprovedFees
                      ? theme.palette.grey[100]
                      : 'transparent',
                  }}
                />
              </div>
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Sales tax rate',
      key: 'salesTaxRate',
      sort: false,
      render: (_, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.salesTaxRate`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <div>
                <AppNumberInput
                  {...field}
                  disabled={hasApprovedFees}
                  decimals={10}
                  minFractionDigits={2}
                  onChange={(value: number) => {
                    field.onChange(value);
                  }}
                  error={!!error}
                  helperText={error?.message}
                  percentEndAdornment
                  style={{
                    backgroundColor: hasApprovedFees
                      ? theme.palette.grey[100]
                      : 'transparent',
                  }}
                />
              </div>
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Fee (inc tax)',
      key: 'feeIncTax',
      sort: false,
      render: (elm) => {
        const feeIncTax = calculateFeeIncTax(elm.feeExTax, elm.salesTaxRate);
        return (
          <Tooltip title={templateInUseMessage}>
            <TextContainer>
              {elm?.type === LpFeeType.OneOffFixed
                ? numberToCurrencyString(feeIncTax)
                : Intl.NumberFormat('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 10,
                  }).format(feeIncTax) + '%'}
            </TextContainer>
          </Tooltip>
        );
      },
    },
    {
      label: 'Action',
      key: 'action',
      sort: false,
      render: (_, i: number) => (
        <Button
          size="small"
          tooltip={
            hasApprovedFees
              ? 'It is not possible to delete a fee template that is currently in use.'
              : undefined
          }
          onClick={() => {
            const updatedFees = fees.filter((_, index) => index !== i);
            reset({
              ...getValues(),
              lpFeeTemplates: getValues().lpFeeTemplates.map(
                (template, templateIndex) =>
                  templateIndex === index
                    ? { ...template, lpFeeTemplateFees: updatedFees }
                    : template,
              ),
            });
            setHasEditedTemplateWithCommitment(hasCommitments);
          }}
          disabled={hasApprovedFees}
        >
          <Delete color={hasApprovedFees ? 'disabled' : undefined} />
        </Button>
      ),
    },
  ];

  return (
    <Spacing>
      <Table
        columns={columns}
        tablebody={fees}
        variant="nohover"
        key={fees.length}
        alignRowsTop
      />

      <ButtonGroup>
        <Button
          size="large"
          onClick={() => {
            append({
              ...emptyFee,
            } as LpFee);
            setHasEditedTemplateWithCommitment(hasCommitments);
          }}
          tooltip={
            hasApprovedFees
              ? 'It is not possible to add a fee to a fee template that is currently in use.'
              : undefined
          }
          color={hasApprovedFees ? 'secondary' : 'primary'}
          variant="outlined"
          disabled={hasApprovedFees}
        >
          <strong>Add fee</strong>
        </Button>
      </ButtonGroup>
    </Spacing>
  );
};

export default FeeTemplateFeesTable;
